import {
  BVA_PDF_FIT_ATTRIBUTE,
  BVA_PDF_MIN_ATTRIBUTE,
  BVA_PDF_PROBE_ATTRIBUTE,
  BVA_PDF_SCOPE_ATTRIBUTE,
} from 'pages/BrandDetails/pdf/bvaPdfLayout'

interface ScopeInfo {
  scope: HTMLElement
  probe: HTMLElement
  axis: 'width' | 'height'
}

interface GroupInfo {
  name: string
  elements: HTMLElement[]
  min: number
  base: number
  low: number
  high: number
  best: number
  candidate: number
}

const OVERFLOW_TOLERANCE_PX = 1
const BINARY_SEARCH_ITERATIONS = 6

const parseNumericStyle = (value: string): number => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const lineHeightPxOf = (element: HTMLElement): number => {
  const computed = window.getComputedStyle(element)
  const lineHeight = parseNumericStyle(computed.lineHeight)
  if (lineHeight > 0 && computed.lineHeight.endsWith('px')) return lineHeight
  // Unitless/'normal' line-height: resolve against the current font-size.
  const fontSize = parseNumericStyle(computed.fontSize)
  return lineHeight > 0 ? lineHeight * fontSize : fontSize * 1.2
}

const applyLineClamp = (element: HTMLElement, lines: number): void => {
  const clampedLines = Math.max(1, Math.round(lines))
  element.style.overflow = 'hidden'
  element.style.display = '-webkit-box'
  element.style.setProperty('-webkit-box-orient', 'vertical')
  element.style.setProperty('-webkit-line-clamp', String(clampedLines))
}

const resolveScopeInfos = (root: HTMLElement): ScopeInfo[] => {
  const scopeElements = Array.from(root.querySelectorAll<HTMLElement>(`[${BVA_PDF_SCOPE_ATTRIBUTE}]`))

  return scopeElements
    .map(scope => {
      const probe = scope.hasAttribute(BVA_PDF_PROBE_ATTRIBUTE)
        ? scope
        : scope.querySelector<HTMLElement>(`[${BVA_PDF_PROBE_ATTRIBUTE}]`)
      const axis = scope.getAttribute('data-fit-axis') === 'width' ? 'width' : 'height'
      return probe ? { scope, probe, axis } : null
    })
    .filter((info): info is ScopeInfo => info !== null)
}

const resetFitElement = (element: HTMLElement): void => {
  element.style.removeProperty('font-size')
  element.style.removeProperty('overflow')
  element.style.removeProperty('display')
  element.style.removeProperty('text-overflow')
  element.style.removeProperty('-webkit-box-orient')
  element.style.removeProperty('-webkit-line-clamp')
}

const buildGroups = (root: HTMLElement): Map<string, GroupInfo> => {
  const fitElements = Array.from(root.querySelectorAll<HTMLElement>(`[${BVA_PDF_FIT_ATTRIBUTE}]`))
  fitElements.forEach(resetFitElement)

  const groups = new Map<string, GroupInfo>()

  fitElements.forEach(element => {
    const name = element.getAttribute(BVA_PDF_FIT_ATTRIBUTE)
    if (!name) return

    const existing = groups.get(name)
    if (existing) {
      existing.elements.push(element)
      return
    }

    const min = parseNumericStyle(element.getAttribute(BVA_PDF_MIN_ATTRIBUTE) ?? '0')
    // The design size, read once before any measurement pass forces a shrink.
    const base = parseNumericStyle(window.getComputedStyle(element).fontSize)
    groups.set(name, {
      name,
      elements: [element],
      min,
      base,
      low: min,
      high: base,
      best: min,
      candidate: base,
    })
  })

  return groups
}

const scopeOverflows = (info: ScopeInfo): boolean => {
  if (info.axis === 'width') {
    return info.probe.scrollWidth - info.probe.clientWidth > OVERFLOW_TOLERANCE_PX
  }
  return info.probe.getBoundingClientRect().height - info.scope.clientHeight > OVERFLOW_TOLERANCE_PX
}

/** The group name(s) whose fit elements live inside this scope. */
const groupsInScope = (scope: HTMLElement, groups: Map<string, GroupInfo>): string[] =>
  Array.from(groups.entries())
    .filter(([, group]) => group.elements.some(element => element.closest(`[${BVA_PDF_SCOPE_ATTRIBUTE}]`) === scope))
    .map(([name]) => name)

const setGroupFontSize = (group: GroupInfo, size: number): void => {
  group.elements.forEach(element => {
    element.style.fontSize = `${size}px`
  })
}

/**
 * Multi-element scopes (the PRODUCT EDGE / BRAND EDGE stacks) that are still
 * too tall after every group has been shrunk to its floor. Reduces whichever
 * paragraph is currently occupying the most lines, one line at a time, until
 * the modelled stack height fits or every paragraph is down to one line.
 * Modelled rather than re-measured: cheap, and this only runs as a backstop
 * once the primary shrink-to-fit pass has already failed.
 */
const clampStackToScope = (scope: HTMLElement, probe: HTMLElement, elements: HTMLElement[]): void => {
  const rects = elements.map(element => element.getBoundingClientRect().height)
  const lineHeights = elements.map(lineHeightPxOf)
  const lines = rects.map((height, index) => Math.max(1, Math.round(height / lineHeights[index])))

  const chrome = probe.getBoundingClientRect().height - rects.reduce((sum, height) => sum + height, 0)
  const budget = scope.clientHeight - chrome

  const modelledHeight = () => lines.reduce((sum, count, index) => sum + count * lineHeights[index], 0)

  let guard = 0
  while (modelledHeight() > budget && guard < 200) {
    let tallestIndex = -1
    let tallestHeight = 0
    lines.forEach((count, index) => {
      const height = count * lineHeights[index]
      if (count > 1 && height > tallestHeight) {
        tallestHeight = height
        tallestIndex = index
      }
    })
    if (tallestIndex === -1) break
    lines[tallestIndex] -= 1
    guard += 1
  }

  elements.forEach((element, index) => applyLineClamp(element, lines[index]))
}

/**
 * Shrinks every `[data-fit-group]` text region until its owning
 * `[data-fit-scope]` box(es) stop overflowing, never growing past the design
 * size and never going below the group's declared floor. Falls back to
 * `-webkit-line-clamp` (or `text-overflow: ellipsis` for the width-based
 * header title) for anything still too big at the floor.
 *
 * `scrollHeight > clientHeight` is NOT used to detect overflow: every scope
 * centres or bottom-aligns its content, so overflow from a centred/
 * bottom-anchored flex child escapes off the *top*, which `scrollHeight` does
 * not register. See bvaPdfLayout.ts for the scope/probe contract this relies
 * on instead.
 *
 * Must run synchronously after the template's fonts and images are ready --
 * measuring against the fallback font or an undecoded image gives wrong
 * answers in both directions.
 */
export const autofitText = (root: HTMLElement): void => {
  const scopeInfos = resolveScopeInfos(root)
  const groups = buildGroups(root)
  if (groups.size === 0 || scopeInfos.length === 0) return

  // Pass 1 (read, post-reset): which scopes overflow at design size, and
  // which groups do they implicate?
  const targetGroupNames = new Set<string>()
  scopeInfos.forEach(info => {
    if (!scopeOverflows(info)) return
    groupsInScope(info.scope, groups).forEach(name => targetGroupNames.add(name))
  })

  const targets = Array.from(targetGroupNames)
    .map(name => groups.get(name))
    .filter((group): group is GroupInfo => Boolean(group))
  if (targets.length === 0) return

  // Pass 2: lock-step binary search. Every candidate is written, then every
  // affected scope is read, so one iteration costs one layout for the whole
  // set rather than one per group.
  for (let iteration = 0; iteration < BINARY_SEARCH_ITERATIONS; iteration += 1) {
    targets.forEach(group => {
      group.candidate = Math.round((group.low + group.high) / 2)
      setGroupFontSize(group, group.candidate)
    })

    targets.forEach(group => {
      const stillOverflows = scopeInfos.some(
        info => scopeOverflows(info) && groupsInScope(info.scope, groups).includes(group.name),
      )
      if (stillOverflows) {
        group.high = group.candidate
      } else {
        group.low = group.candidate
        group.best = group.candidate
      }
    })
  }

  // Pass 3 (write): commit the largest size that fit, or the floor.
  targets.forEach(group => setGroupFontSize(group, group.best))

  // Pass 4: anything still overflowing at the floor gets clamped instead of
  // silently clipped by `overflow: hidden`.
  scopeInfos.forEach(info => {
    if (!scopeOverflows(info)) return

    if (info.axis === 'width') {
      info.probe.style.textOverflow = 'ellipsis'
      return
    }

    const namesInScope = groupsInScope(info.scope, groups)
    const elementsInScope = namesInScope.flatMap(name => {
      const group = groups.get(name)
      if (!group) return []
      return group.elements.filter(element => element.closest(`[${BVA_PDF_SCOPE_ATTRIBUTE}]`) === info.scope)
    })

    if (elementsInScope.length === 0) return

    if (elementsInScope.length === 1) {
      const [element] = elementsInScope
      const lineHeight = lineHeightPxOf(element)
      const rectHeight = element.getBoundingClientRect().height
      const linesNow = Math.max(1, Math.round(rectHeight / lineHeight))
      const excessLines = Math.max(1, Math.ceil((rectHeight - info.scope.clientHeight) / lineHeight))
      applyLineClamp(element, Math.max(1, linesNow - excessLines))
      return
    }

    clampStackToScope(info.scope, info.probe, elementsInScope)
  })
}
